import { OpenIdIdentityProviderModel } from '../../models/openid-identity-provider.model'
import { Well } from '@pushly/aqe/lib/components/index'
import { InfoCircleOutlined } from '@ant-design/icons'
import StackedTagSelect from '@pushly/aqe/lib/components/stacked-tag-select/stacked-tag-select'
import React from 'react'
import { IdpFormMode } from '../../enums/idp-form-modes.enum'
import { Divider, Form, Input, Space, Typography } from 'antd'

interface IProps {
    workingModel: OpenIdIdentityProviderModel
    setWorkingModel: React.Dispatch<React.SetStateAction<OpenIdIdentityProviderModel>>
    loadingProvider: boolean
    formMode: string
}

export const MicrosoftAzureADProviderForm = (props: IProps) => {
    const { workingModel, setWorkingModel, loadingProvider, formMode } = props
    return (
        <div className="sso-provider-form">
            <Well title="Administrator Set Up Instructions" variant="ghost" padding="none" hideFooter={true}>
                <p>
                    Your Microsoft Azure AD administrator will need to set up an App Registration within Azure to enable
                    this provider. Please provide a link to our&nbsp;
                    <a
                        href="https://documentation.pushly.com/platform/organizations/single-sign-on/microsoft-azure-ad"
                        target="_blank"
                    >
                        Microsoft Azure AD Provider documentation
                    </a>
                    &nbsp;to your Azure administrator.
                </p>
            </Well>

            <Divider size="small" />

            <Well
                title="Provider Configuration"
                variant="ghost"
                padding="none"
                hideFooter={true}
                loading={loadingProvider}
            >
                <Form.Item label="Name">
                    <Input
                        size="small"
                        placeholder="Microsoft Azure AD"
                        value={workingModel.getName() ?? undefined}
                        onChange={(ev) => {
                            const name = ev.target.value
                            const updated = workingModel.clone()
                            updated.setName(name)
                            setWorkingModel(updated)
                        }}
                    />
                </Form.Item>
                <Form.Item label="Azure AD Domain">
                    <Input
                        size="small"
                        placeholder="your-azure-ad-domain.com"
                        disabled={formMode === IdpFormMode.EDIT}
                        value={workingModel.getDomain() ?? undefined}
                        onChange={(ev) => {
                            const azureDomain = ev.target.value
                            const updated = workingModel.clone()
                            updated.setDomain(azureDomain)
                            setWorkingModel(updated)
                        }}
                    />
                </Form.Item>
                <Form.Item label="Client ID">
                    <Input
                        size="small"
                        disabled={formMode === IdpFormMode.EDIT}
                        placeholder={formMode === IdpFormMode.EDIT ? '********' : undefined}
                        value={workingModel.getClientId() ?? undefined}
                        onChange={(ev) => {
                            const clientId = ev.target.value
                            const updated = workingModel.clone()
                            updated.setClientId(clientId)
                            setWorkingModel(updated)
                        }}
                    />
                </Form.Item>
                <Form.Item label="Client Secret">
                    <Input
                        size="small"
                        disabled={formMode === IdpFormMode.EDIT}
                        placeholder={formMode === IdpFormMode.EDIT ? '********' : undefined}
                        value={workingModel.getClientSecret() ?? undefined}
                        onChange={(ev) => {
                            const clientSecret = ev.target.value
                            const updated = workingModel.clone()
                            updated.setClientSecret(clientSecret)
                            setWorkingModel(updated)
                        }}
                    />
                </Form.Item>
                <Form.Item
                    label="Email Domains"
                    extra={
                        <Space align="start">
                            <InfoCircleOutlined className="info-icon" />
                            <Typography.Paragraph type="secondary" className="font-size--s">
                                Enter each domain on a new line that should use this identity provider. For example: If
                                your email is user@yourcompany.com then you would enter yourcompany.com.
                            </Typography.Paragraph>
                        </Space>
                    }
                >
                    <StackedTagSelect<string[]>
                        size="small"
                        mode="tags"
                        className="associated-domains"
                        dropdownClassName="associated-domains--select-dropdown"
                        placeholder="yourcompany.com"
                        tokenSeparators={[',', ';', ' ', '\n']}
                        showOptions={false}
                        notFoundContent=""
                        value={workingModel.getAssociatedEmailDomains()}
                        onChange={(v) => {
                            const associatedEmailDomains = v
                            const updated = workingModel.clone()
                            updated.setAssociatedEmailDomains(associatedEmailDomains)
                            setWorkingModel(updated)
                        }}
                    />
                </Form.Item>
            </Well>
        </div>
    )
}
